import { useId } from 'react';

import { Spinner } from './Spinner';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  /** Shows a spinner instead of the knob and blocks interaction, without
   * shifting layout the way swapping the whole row for a loader would. */
  loading?: boolean;
}

/**
 * Accessible on/off toggle. Native-button based (not `<input type="checkbox">`)
 * so it can express `role="switch"` + `aria-checked` per WAI-ARIA, and is fully
 * keyboard-operable (Space/Enter — native `<button>` handles both natively).
 * RTL-safe: the knob position is driven by `inset-inline-start`/`transform`
 * only, no hardcoded left/right, and it mirrors correctly under `dir="rtl"`.
 */
export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  loading = false,
}: SwitchProps): JSX.Element {
  const id = useId();
  const labelId = `${id}-label`;
  const descId = description ? `${id}-desc` : undefined;
  const isDisabled = disabled || loading;

  return (
    <div className="switch-row">
      <div className="switch-row__text">
        {/* A <label for> does NOT name a <button role="switch"> (the HTML-AAM
            accessible-name algorithm only maps <label> to form controls), so the
            button is named via aria-labelledby pointing at this label's id. */}
        <label id={labelId} className="switch-row__label" htmlFor={id}>
          {label}
        </label>
        {description ? (
          <p id={descId} className="switch-row__description">
            {description}
          </p>
        ) : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={descId}
        disabled={isDisabled}
        className={`switch${checked ? ' switch--on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        {loading ? (
          <span className="switch__spinner">
            <Spinner />
          </span>
        ) : (
          <span className="switch__knob" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
